import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useSubAccounts } from '@src/providers/SubAccountsContext';
import { PositionAssetTypeEnum, PositionUpdateSourceEnum, type PersistedDefaultTabProps } from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { MARKET_ACCOUNTS_TAB_LABEL } from './positionsTabLabels';
import {
  BLOTTER_ID_POSITIONS_SUBACCOUNT,
  BLOTTER_ID_PREDICTION_POSITIONS,
  BLOTTER_ID_SPOT_POSITIONS,
  BLOTTER_ID_UNIFIED_DERIVATIVES,
  BLOTTER_ID_UNIFIED_SPOT_AND_DERIVS,
} from './tokens';
import type {
  DerivativesPositionsTabProps,
  PositionColumn,
  PositionsTabProps,
  PositionsTabType,
  PositionsTableFilter,
  PredictionsPositionsTabProps,
  SubAccountPositionsTabProps,
  UnifiedPositionColumn,
  UnifiedPositionsTabProps,
} from './types';

export const defaultDerivativesBlotterColumns = [
  'reconWarning',
  { id: 'underlying', initialRowGroup: true, initialHide: true },
  { id: 'instrument' },
  'MarketAccount',
  'Amount',
  'AvgCost',
  'MarkPrice',
  'UnderlyingPrice',
  'LiquidationPrice',
  'elpVsMp',
  'Delta',
  'Equivalent.Delta',
  'UnrealizedPnL',
  { field: 'AssetType', initialRowGroup: true, initialHide: true },
] satisfies PositionColumn[];

export const defaultDerivativesBlotterFilter: PositionsTableFilter = {
  Source: PositionUpdateSourceEnum.Gateway,
};

export const defaultDerivativesBlotterTab: Omit<DerivativesPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultDerivativesBlotterColumns,
  defaultFilter: defaultDerivativesBlotterFilter,
  tabType: 'Derivatives',
  showZeroBalances: false,
};

export const defaultUnifiedBlotterColumns = [
  'reconWarning',
  { id: 'MarketAccount', initialRowGroup: true, initialRowGroupIndex: 0, initialHide: true },
  { id: 'underlying', initialRowGroup: true, initialRowGroupIndex: 1, initialHide: true },
  { id: 'Asset', initialRowGroup: true, initialRowGroupIndex: 2, initialHide: true },
  'Amount',
  'Equivalent.Amount',
  'Equivalent.Delta',
  'AvgCost',
  'MarkPrice',
  'UnderlyingPrice',
  'Equivalent.UnrealizedPnL',
  'venueEquityEquivalent',
  'talosEquityEquivalent',
  'Equivalent.initialMargin',
  'Equivalent.maintenanceMargin',
  'LiquidationPrice',
  'elpVsMp',
] satisfies UnifiedPositionColumn[];

export const defaultUnifiedBlotterFilter: PositionsTableFilter = {
  Source: PositionUpdateSourceEnum.Gateway,
};

export const defaultUnifiedBlotterTab: Omit<UnifiedPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultUnifiedBlotterColumns,
  defaultFilter: defaultUnifiedBlotterFilter,
  tabType: 'Unified',
  showZeroBalances: false,
};

export const defaultSubAccountsBlotterColumns = [
  { field: 'SubAccount', id: 'SubAccount', initialRowGroup: true, initialHide: true, initialSort: '+' },
  'Asset',
  'Amount',
  'Equivalent.Amount',
  'AvgCost',
  'MarkPrice',
  'Delta',
  'Equivalent.Delta',
  'Equivalent.CumFees',
  'Equivalent.UnrealizedPnL',
  'Equivalent.RealizedPnL',
  'Equivalent.FundingPnL',
  'Equivalent.TotalPnL',
] satisfies PositionColumn[];

export const defaultSubAccountsBlotterFilter: PositionsTableFilter = {};

export const defaultSubAccountsBlotterTab: Omit<SubAccountPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultSubAccountsBlotterColumns,
  defaultFilter: defaultSubAccountsBlotterFilter,
  tabType: 'Sub Accounts',
  showZeroBalances: false,
};

export const defaultPredictionsBlotterColumns = [
  { id: 'predictionCategory', initialRowGroup: true, initialRowGroupIndex: 0, initialHide: true },
  { id: 'predictionSeries', initialRowGroup: true, initialRowGroupIndex: 1, initialHide: true },
  { id: 'predictionEvent', initialRowGroup: true, initialRowGroupIndex: 2, initialHide: true },
  { id: 'instrument' },
  'MarketAccount',
  'Amount',
  'AvgCost',
  'MarkPrice',
  'UnrealizedPnL',
] satisfies PositionColumn[];

export const defaultPredictionsBlotterFilter: PositionsTableFilter = {
  AssetTypes: [PositionAssetTypeEnum.PredictionOutcome],
  Source: PositionUpdateSourceEnum.Gateway,
};

export const defaultPredictionsBlotterTab: Omit<PredictionsPositionsTabProps, 'id' | 'label'> = {
  defaultColumns: defaultPredictionsBlotterColumns,
  defaultFilter: defaultPredictionsBlotterFilter,
  tabType: 'Predictions',
  showZeroBalances: false,
};

export function getDefaultPositionsTabProperties(tabType: PositionsTabType) {
  switch (tabType) {
    case 'Derivatives':
      return defaultDerivativesBlotterTab;
    case 'Sub Accounts':
      return defaultSubAccountsBlotterTab;
    case 'Unified':
      return defaultUnifiedBlotterTab;
    case 'Predictions':
      return defaultPredictionsBlotterTab;
    default: {
      const _exhaustive: never = tabType;
      return _exhaustive;
    }
  }
}

export const POSITIONS_TABS_EXCLUDED_IDS = [
  BLOTTER_ID_POSITIONS_SUBACCOUNT,
  BLOTTER_ID_UNIFIED_DERIVATIVES,
  BLOTTER_ID_SPOT_POSITIONS,
  BLOTTER_ID_UNIFIED_SPOT_AND_DERIVS,
  BLOTTER_ID_PREDICTION_POSITIONS,
];

export function isEnabledPositionsTabType(tabType: string, enabledTabTypes: readonly PositionsTabType[]): boolean {
  return (enabledTabTypes as readonly string[]).includes(tabType);
}

/** Mobile has no Predictions blotter, so it further drops that type even when the flag is on. */
export function isVisibleMobilePositionsTab(
  tab: { tabType: string },
  enabledTabTypes: readonly PositionsTabType[]
): boolean {
  return isEnabledPositionsTabType(tab.tabType, enabledTabTypes) && tab.tabType !== 'Predictions';
}

export function useEnabledPositionsTabTypes(): PositionsTabType[] {
  const {
    enableDerivativesPositionsBlotter,
    enableUnifiedPositionsBlotter,
    enablePredictionMarkets,
    enableLegacyPositionsTabs,
  } = useFeatureFlag();
  const { allSubAccounts } = useSubAccounts();
  const enableSubAccountsPositionsBlotter = allSubAccounts != null && allSubAccounts.length > 0;
  const showDerivativesPositionsBlotter = enableLegacyPositionsTabs && enableDerivativesPositionsBlotter;

  return useMemo(
    () =>
      compact<PositionsTabType>([
        showDerivativesPositionsBlotter ? 'Derivatives' : undefined,
        enableUnifiedPositionsBlotter ? 'Unified' : undefined,
        enableSubAccountsPositionsBlotter ? 'Sub Accounts' : undefined,
        enablePredictionMarkets ? 'Predictions' : undefined,
      ]),
    [
      showDerivativesPositionsBlotter,
      enableUnifiedPositionsBlotter,
      enableSubAccountsPositionsBlotter,
      enablePredictionMarkets,
    ]
  );
}

export function useDefaultPositionsTabs(): PersistedDefaultTabProps<PositionsTabProps>[] {
  const {
    enableDerivativesPositionsBlotter,
    enableUnifiedPositionsBlotter,
    enablePredictionMarkets,
    enableLegacyPositionsTabs,
  } = useFeatureFlag();
  const { allSubAccounts } = useSubAccounts();
  const enableSubAccountsPositionsBlotter = allSubAccounts != null && allSubAccounts.length > 0;
  const showDerivativesPositionsBlotter = enableLegacyPositionsTabs && enableDerivativesPositionsBlotter;

  return useMemo(() => {
    const tabs: PersistedDefaultTabProps<PositionsTabProps>[] = [];

    if (showDerivativesPositionsBlotter) {
      tabs.push({
        label: 'Derivatives',
        id: BLOTTER_ID_UNIFIED_DERIVATIVES,
        ...defaultDerivativesBlotterTab,
      });
    }

    if (enableUnifiedPositionsBlotter) {
      tabs.push({
        label: MARKET_ACCOUNTS_TAB_LABEL,
        id: BLOTTER_ID_UNIFIED_SPOT_AND_DERIVS,
        ...defaultUnifiedBlotterTab,
      });
    }

    if (enableSubAccountsPositionsBlotter) {
      tabs.push({
        label: 'Sub Accounts',
        id: BLOTTER_ID_POSITIONS_SUBACCOUNT,
        ...defaultSubAccountsBlotterTab,
      });
    }

    if (enablePredictionMarkets) {
      tabs.push({
        label: 'Predictions',
        id: BLOTTER_ID_PREDICTION_POSITIONS,
        ...defaultPredictionsBlotterTab,
      });
    }

    return tabs;
  }, [
    showDerivativesPositionsBlotter,
    enableUnifiedPositionsBlotter,
    enableSubAccountsPositionsBlotter,
    enablePredictionMarkets,
  ]);
}
